import { useQuery } from '@tanstack/react-query';
import { fetchTestSubleases } from '../services/test/test-subleases';
import { TestSubleaseResponse, TestSublease } from '../types/test-sublease';
import { useMemo } from 'react';
import { supabaseLeaseLotService } from '../services/supabase/lease-lots'; // Import the service

export interface TestSubLeaseFilter {
  holder: string | null;
  endDateFrom: string | null;
  endDateTo: string | null;
  leaseDealing: string | null;
}

export interface TestSortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * Custom hook for fetching sublease data for the test page
 * Now with filtering and sorting capabilities
 */
export function useTestSubLeases(
  filters: TestSubLeaseFilter = { holder: null, endDateFrom: null, endDateTo: null, leaseDealing: null },
  sortConfig: TestSortConfig = { field: 'lease_id', direction: 'asc' },
  searchQuery: string = ''
): TestSubleaseResponse {
  // Fetch the data
  const { data, error, isLoading } = useQuery({
    queryKey: ['testSubleasesWithAssociatedLots'], // Changed queryKey to reflect new data shape
    queryFn: async () => {
      const initialResult = await fetchTestSubleases();
      if (initialResult.error || !initialResult.data) {
        throw initialResult.error || new Error('Failed to fetch initial subleases');
      }
      const subleases: TestSublease[] = initialResult.data;

      if (subleases.length === 0) {
        return [];
      }

      // Collect all unique, valid lease_ids
      const leaseIds = [
        ...new Set(
          subleases
            .map(s => s.lease_id)
            .filter(id => id != null) // Filter out null or undefined
            .map(id => Number(id)) // Convert to number
            .filter(id => !isNaN(id)) // Filter out NaN results from Number conversion
        ),
      ];

      let lotsByLeaseId: Record<number, { leaselot_lot: string }[]> = {};
      if (leaseIds.length > 0) {
        lotsByLeaseId = await supabaseLeaseLotService.getLotsForMultipleLeaseIds(leaseIds);
      }

      // Augment each sublease with associated lot IDs string
      const augmentedSubleases = subleases.map(sublease => {
        const numericLeaseId = Number(sublease.lease_id);
        const associatedLots = (isNaN(numericLeaseId) || !lotsByLeaseId[numericLeaseId])
          ? []
          : lotsByLeaseId[numericLeaseId];
        
        const lotIdsString = associatedLots.map(lot => lot.leaselot_lot).join(', ');
        
        return {
          ...sublease,
          associatedLotIdsString: lotIdsString || '', // Ensure it's always a string
        };
      });
      
      return augmentedSubleases;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Apply filtering and sorting
  const filteredAndSortedData = useMemo(() => {
    if (!data) return null;

    console.log('Filtering and sorting test subleases data:', { filters, sortConfig, searchQuery });

    let filteredLeases = [...data];

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredLeases = filteredLeases.filter(lease => 
        (lease.lease_id?.toString().toLowerCase() || '').includes(query) ||
        (lease.lease_dealing?.toLowerCase() || '').includes(query) ||
        (lease.holder_name?.toLowerCase() || '').includes(query) ||
        (lease.associatedLotIdsString?.toLowerCase() || '').includes(query) // Added search for associated lots
      );
    }

    // Apply filters
    if (filters.holder) {
      filteredLeases = filteredLeases.filter(lease => 
        lease.holder_name === filters.holder
      );
    }

    if (filters.endDateFrom) {
      const fromDate = new Date(filters.endDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      
      filteredLeases = filteredLeases.filter(lease => {
        if (!lease.lease_end) return false;
        
        const leaseEndDate = new Date(lease.lease_end);
        leaseEndDate.setHours(0, 0, 0, 0);
        
        return leaseEndDate >= fromDate;
      });
    }

    if (filters.endDateTo) {
      const toDate = new Date(filters.endDateTo);
      toDate.setHours(23, 59, 59, 999);
      
      filteredLeases = filteredLeases.filter(lease => {
        if (!lease.lease_end) return false;
        
        const leaseEndDate = new Date(lease.lease_end);
        
        return leaseEndDate <= toDate;
      });
    }

    if (filters.leaseDealing) {
      filteredLeases = filteredLeases.filter(lease => 
        lease.lease_dealing === filters.leaseDealing
      );
    }

    // Apply sorting
    const sortedLeases = [...filteredLeases].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;

      // Helper function to safely compare strings
      const compareStrings = (strA: string | null | undefined, strB: string | null | undefined) => {
        return direction * ((strA || '').localeCompare(strB || ''));
      };

      // Helper function to safely compare numbers
      const compareNumbers = (numA: number | string | null | undefined, numB: number | string | null | undefined) => {
        if (numA === undefined || numA === null) return direction;
        if (numB === undefined || numB === null) return -direction;
        return direction * (Number(numA) - Number(numB));
      };

      // Helper function to safely compare dates
      const compareDates = (dateA: string | null | undefined, dateB: string | null | undefined) => {
        if (!dateA) return direction;
        if (!dateB) return -direction;
        return direction * (new Date(dateA).getTime() - new Date(dateB).getTime());
      };

      switch (sortConfig.field) {
        case 'lease_id':
          return compareNumbers(a.lease_id, b.lease_id);
        case 'lease_holder':
          return compareNumbers(a.lease_holder, b.lease_holder);
        case 'holder_name':
          return compareStrings(a.holder_name, b.holder_name);
        case 'lease_start':
          return compareDates(a.lease_start, b.lease_start);
        case 'lease_end':
          return compareDates(a.lease_end, b.lease_end);
        case 'lease_method':
          return compareStrings(a.lease_method, b.lease_method);
        case 'lease_type':
          return compareStrings(a.lease_type, b.lease_type);
        case 'lease_furth_term':
          return compareStrings(a.lease_furth_term, b.lease_furth_term);
        case 'lease_rent_freq':
          return compareStrings(a.lease_rent_freq, b.lease_rent_freq);
        case 'lease_dealing':
          return compareStrings(a.lease_dealing, b.lease_dealing);
        case 'lease_deal_doc':
          return compareStrings(a.lease_deal_doc, b.lease_deal_doc);
        default:
          return 0;
      }
    });

    return sortedLeases;
  }, [data, filters, sortConfig, searchQuery]);

  return {
    data: filteredAndSortedData || null, // Convert undefined to null to match our type
    error: error as Error | null,
    isLoading,
  };
}
